import { useEffect } from "react";
import axios from "axios";
import AuthService from "./AuthService";
import {
  isSubscriptionBlocked,
  isSubscriptionBypassUrl,
  markSubscriptionInactive,
} from "../../../shared/utils/subscriptionAccessStore";

const AxiosInterceptorSetup = ({ children }) => {
  useEffect(() => {
    const requestId = axios.interceptors.request.use(
      (config) => {
        if (
          isSubscriptionBlocked() &&
          !isSubscriptionBypassUrl(config?.url)
        ) {
          return Promise.reject(new axios.Cancel("Subscription inactive"));
        }
        return AuthService.prepareAuthenticatedRequest(config);
      },
      (error) => Promise.reject(error)
    );

    const responseId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (axios.isCancel(error)) {
          return Promise.reject(error);
        }

        const status = error.response?.status;
        const originalRequest = error.config;

        if (status === 402) {
          markSubscriptionInactive();
          if (
            typeof window !== "undefined" &&
            !window.location.pathname.includes("/subscription-inactive")
          ) {
            window.location.assign("/subscription-inactive");
          }
          return Promise.reject(error);
        }

        if (!originalRequest || status !== 401 || originalRequest._retry) {
          return Promise.reject(error);
        }

        originalRequest._retry = true;
        try {
          const response = await AuthService.refreshTokens();
          const token = response?.data?.newAccessToken;
          if (!token) {
            AuthService.handleLogout();
            return Promise.reject(error);
          }
          AuthService.setSessionFromAccessToken(token);
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${token}`;
          originalRequest.withCredentials = true;
          return axios(originalRequest);
        } catch (refreshError) {
          AuthService.handleLogout();
          return Promise.reject(refreshError);
        }
      }
    );

    return () => {
      axios.interceptors.request.eject(requestId);
      axios.interceptors.response.eject(responseId);
    };
  }, []);

  return children;
};

export default AxiosInterceptorSetup;
